<script setup lang="ts">
import { ref, watch } from 'vue'
import { VBadge, VButton } from '@/modules/shared/components'
import { useTaskListStore } from '../stores/tasking-store'
import Task from './Task.vue'

const props = defineProps<{
  taskListId: number
}>()

const store = useTaskListStore()

// Fetch the task list when the taskListId changes
watch(
  () => props.taskListId,
  async (newTaskListId) => {
    if (newTaskListId) {
      await store.fetchTaskList(newTaskListId)
    }
  },
  { immediate: true },
)

const expandedIndex = ref<number | null>(null)

const isExpanded = (index: number) => expandedIndex.value === index

const toggleExpand = (index: number) => {
  expandedIndex.value = expandedIndex.value === index ? null : index
}

// Set the expanded index
watch(
  () => store.taskList,
  (newTaskList) => {
    // If the current expanded task is incomplete, do not change the expanded index
    if (expandedIndex.value !== null) {
      const task = newTaskList?.tasks[expandedIndex.value]
      if (task && store.isTaskIncomplete(task)) {
        return
      }
    }

    if (newTaskList?.tasks?.length > 0) {
      const firstIncompleteTask = newTaskList.tasks.find((task) => !store.isTaskComplete(task))
      if (firstIncompleteTask) {
        expandedIndex.value = newTaskList.tasks.indexOf(firstIncompleteTask)
      }
    }
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <transition name="fold">
    <div
      v-if="store.taskList && store.taskList?.is_visible"
      class="fold-content animate-fade-in mb-12 rounded-lg border bg-gradient-to-br from-white via-gray-50 to-gray-50 p-4 text-gray-800"
    >
      <div class="items-top flex justify-between">
        <div>
          <h2 class="text-lg font-medium text-gray-800">
            {{ store.taskList?.name }}
          </h2>
          <p class="mt-1 text-sm text-gray-700">{{ store.taskList?.description }}</p>
        </div>
        <div class="flex items-center gap-2">
          <VBadge color="v-green">
            <span>{{ store.taskList?.tasks_complete_count }} / {{ store.taskList?.tasks_count }} Completed</span>
          </VBadge>
          <VButton :click="() => store.completeTaskList(taskListId)">Complete and Close</VButton>
        </div>
      </div>

      <div class="mt-6 space-y-2">
        <Task
          v-for="(task, index) in store.taskList?.tasks"
          :key="task.id"
          :isExpanded="isExpanded(index)"
          :isLoading="store.isLoading(task.id)"
          :task="task"
          @click.prevent="toggleExpand(index)"
        />
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fold-enter-active,
.fold-leave-active {
  transition:
    max-height 0.4s ease,
    opacity 0.3s ease;
}
.fold-enter-from,
.fold-leave-to {
  max-height: 0;
  opacity: 0;
}
.fold-enter-to,
.fold-leave-from {
  max-height: 1000px; /* Adjust depending on content */
  opacity: 1;
}
</style>
